/**
 * Carnaf accident-flow wizard.
 *
 * Walks the consumer through the backend state machine (Week 17):
 *   ACCIDENT_INITIATED → SAFETY_CHECK → LOCATION_CAPTURED →
 *   PHOTO_CAPTURE_IN_PROGRESS → PARTY_EXCHANGE_OPTIONAL →
 *   CLAIM_PREFILE → DISPATCH → RESOLVED
 *
 * The "Talk to a person" escape hatch is rendered on every non-terminal
 * screen per eng plan §8.3. The mandatory disclaimer banner ("This is an
 * automated assistant. If anyone is injured, hang up and dial 101...") is
 * shown at the top until the user moves past SAFETY_CHECK.
 *
 * State is local to the page; the source of truth is the backend's
 * DriverSession.auditTrail (we read the latest state from each tool
 * response). Refreshing the page mid-flow loses local UI state, but the
 * backend session persists — a future iteration could re-derive the
 * current step from the audit trail.
 */

import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Layout } from '@/components/Layout';
import {
  ApiError,
  accidentInitialize,
  accidentUploadPhoto,
  accidentExchangeParty,
  accidentPrefileClaim,
  accidentRequestTow,
  accidentRequestReplacement,
  accidentEscalate,
  emitClaimPacket,
  requestUploadUrl,
  type AccidentFlowState,
  type CaptureGuidance,
} from '@/lib/api';
import { Icon } from '@/components/kol/Icon';
import { getGeo } from '@/lib/geo';
import { maybeCompressImage } from '@/lib/image';

// Ordered photo prompts. Each one nudges the driver to a specific angle so
// the carrier has a complete claim packet without the driver having to
// remember the sequence. Per consumer-onboarding-flow §6.
const PHOTO_PROMPTS: { guidance: CaptureGuidance; label: string }[] = [
  { guidance: 'front', label: 'accident.photos.front' },
  { guidance: 'rear', label: 'accident.photos.rear' },
  { guidance: 'damage_close', label: 'accident.photos.damage' },
  { guidance: 'plate', label: 'accident.photos.plate' },
];

interface ClaimSummary {
  claimReference?: string;
  packetReference?: string;
  tow?: { dispatchId: string; etaMinutes: number };
  replacement?: { dispatchId: string; etaMinutes: number };
}

export function AccidentFlowPage() {
  const { t, i18n } = useTranslation();
  const { id: vehicleId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [state, setState] = useState<AccidentFlowState | 'NOT_STARTED'>('NOT_STARTED');
  const [flowId, setFlowId] = useState<string | null>(null);
  const [photosTaken, setPhotosTaken] = useState(0);
  const [claim, setClaim] = useState<ClaimSummary>({});
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  if (!vehicleId) return null;

  async function onStart() {
    setError(null);
    setPending(true);
    try {
      const result = await accidentInitialize({
        vehicleId: vehicleId!,
        safetyCheckResult: true,
        language: (i18n.language as 'he' | 'en' | 'ar') ?? 'he',
      });
      setFlowId(result.flowId);
      setState(result.state);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setPending(false);
    }
  }

  async function onInjuryReported() {
    setError(null);
    setPending(true);
    try {
      const result = await accidentInitialize({
        vehicleId: vehicleId!,
        safetyCheckResult: false,
        language: (i18n.language as 'he' | 'en' | 'ar') ?? 'he',
      });
      setFlowId(result.flowId);
      setState(result.state);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setPending(false);
    }
  }

  async function onPhotoCaptured(file: File, guidance: CaptureGuidance) {
    if (!flowId) return;
    setError(null);
    setPending(true);
    try {
      // 1) Compress the photo so the upload is fast on cellular.
      const compressed = await maybeCompressImage(file);

      // 2) Get a presigned upload URL via the existing documents pipeline.
      const upload = await requestUploadUrl({
        documentType: 'OTHER',
        fileMimeType: compressed.type,
        fileSizeBytes: compressed.size,
      });

      // 3) Try the actual PUT. In dev the storage-stub URL won't accept the
      //    upload, so we record the storageKey-derived URL anyway so the
      //    audit trail captures the intent.
      const looksReal = !upload.uploadUrl.includes('storage-stub.local');
      if (looksReal) {
        await fetch(upload.uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': compressed.type },
          body: compressed,
        }).catch(() => {
          /* tolerated in dev */
        });
      }

      // 4) Snapshot geo (best-effort; non-blocking).
      const geo = await getGeo();

      // 5) Hand the URL to the accident state machine.
      const result = await accidentUploadPhoto({
        flowId,
        photoUrl: upload.uploadUrl.split('?')[0] ?? upload.uploadUrl,
        geo: geo ?? undefined,
        captureGuidance: guidance,
      });
      setState(result.state);
      setPhotosTaken((n) => n + 1);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setPending(false);
    }
  }

  async function onPartySubmit(partyData: {
    name?: string;
    plate?: string;
    insurerName?: string;
    insurancePolicy?: string;
    phone?: string;
  }) {
    if (!flowId) return;
    setError(null);
    setPending(true);
    try {
      const result = await accidentExchangeParty({ flowId, partyData });
      setState(result.state);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setPending(false);
    }
  }

  async function onSkipParty() {
    if (!flowId) return;
    setError(null);
    setPending(true);
    try {
      // No-other-party still requires we transition out of
      // PHOTO_CAPTURE_IN_PROGRESS. The backend's exchange_party endpoint
      // accepts an empty partyData object.
      const result = await accidentExchangeParty({ flowId, partyData: {} });
      setState(result.state);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setPending(false);
    }
  }

  async function onPrefileClaim() {
    if (!flowId) return;
    setError(null);
    setPending(true);
    try {
      const result = await accidentPrefileClaim(flowId);
      setState(result.state);
      setClaim((c) => ({ ...c, claimReference: result.claimReference }));

      // Fire-and-mostly-forget: emit the structured packet to the carrier.
      // We don't block the user on email delivery; a follow-up screen surfaces
      // the packetReference once it lands.
      void emitClaimPacket(flowId, 'Phoenix')
        .then((r) => setClaim((c) => ({ ...c, packetReference: r.packetReference })))
        .catch(() => {
          /* logged server-side */
        });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setPending(false);
    }
  }

  async function onRequestTow() {
    if (!flowId) return;
    setError(null);
    setPending(true);
    try {
      const geo = (await getGeo()) ?? { lat: 0, lng: 0 };
      const result = await accidentRequestTow(flowId, geo);
      setState(result.state);
      setClaim((c) => ({
        ...c,
        tow: { dispatchId: result.dispatchId, etaMinutes: result.etaMinutes },
      }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setPending(false);
    }
  }

  async function onRequestReplacement() {
    if (!flowId) return;
    setError(null);
    setPending(true);
    try {
      const geo = (await getGeo()) ?? undefined;
      const result = await accidentRequestReplacement(flowId, geo);
      setState(result.state);
      setClaim((c) => ({
        ...c,
        replacement: { dispatchId: result.dispatchId, etaMinutes: result.etaMinutes },
      }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setPending(false);
    }
  }

  async function onEscalate(reason: string) {
    if (!flowId) {
      // No flow yet — bounce the user to a generic emergency screen.
      setState('ESCALATED_TO_HUMAN');
      return;
    }
    setError(null);
    setPending(true);
    try {
      const result = await accidentEscalate(flowId, reason);
      setState(result.state);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setPending(false);
    }
  }

  const showDisclaimer = state !== 'ESCALATED_TO_HUMAN' && state !== 'RESOLVED';

  return (
    <Layout
      title={t('accident.title')}
      trailing={
        <Link
          to={`/kol/vehicles/${vehicleId}`}
          aria-label={t('common.close', { defaultValue: 'סגירה' })}
          className="w-10 h-10 rounded-full border border-carnaf-line bg-carnaf-paper-elevated grid place-items-center text-carnaf-ink-muted shadow-kol-1"
        >
          <Icon name="close" size={19} />
        </Link>
      }
    >
      {showDisclaimer && <DisclaimerBanner onEmergency={() => void onEscalate('user_requested')} />}

      {error && (
        <p role="alert" className="mt-4 rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-800">
          {error}
        </p>
      )}

      <div className="mt-4">
        {state === 'NOT_STARTED' && <StartStep onContinue={onStart} onInjury={onInjuryReported} pending={pending} />}
        {(state === 'SAFETY_CHECK' || state === 'LOCATION_CAPTURED' || state === 'PHOTO_CAPTURE_IN_PROGRESS') && (
          <PhotoStep
            promptIndex={Math.min(photosTaken, PHOTO_PROMPTS.length - 1)}
            photosTaken={photosTaken}
            totalPrompts={PHOTO_PROMPTS.length}
            onPhoto={(file) =>
              onPhotoCaptured(file, PHOTO_PROMPTS[Math.min(photosTaken, PHOTO_PROMPTS.length - 1)]!.guidance)
            }
            onDone={onSkipParty}
            pending={pending}
          />
        )}
        {state === 'PARTY_EXCHANGE_OPTIONAL' && (
          <PartyStep onSubmit={onPartySubmit} onSkip={onPrefileClaim} pending={pending} />
        )}
        {state === 'CLAIM_PREFILE' && (
          <ClaimStep
            claim={claim}
            onRequestTow={onRequestTow}
            onRequestReplacement={onRequestReplacement}
            onDone={() => navigate(`/kol/vehicles/${vehicleId}`, { replace: true })}
            pending={pending}
          />
        )}
        {state === 'DISPATCH' && (
          <ClaimStep
            claim={claim}
            onRequestTow={onRequestTow}
            onRequestReplacement={onRequestReplacement}
            onDone={() => navigate(`/kol/vehicles/${vehicleId}`, { replace: true })}
            pending={pending}
          />
        )}
        {state === 'RESOLVED' && (
          <ResolvedStep claim={claim} onClose={() => navigate(`/kol/vehicles/${vehicleId}`, { replace: true })} />
        )}
        {state === 'ESCALATED_TO_HUMAN' && <EscalatedStep />}
      </div>

      {/* "Talk to a person" escape on non-terminal screens */}
      {flowId && state !== 'ESCALATED_TO_HUMAN' && state !== 'RESOLVED' && (
        <button
          onClick={() => void onEscalate('user_requested')}
          disabled={pending}
          className="mt-8 w-full text-sm text-carnaf-ink-muted underline"
        >
          {t('accident.talk_to_person')}
        </button>
      )}
    </Layout>
  );
}

// --- sub-components ---

function DisclaimerBanner({ onEmergency }: { onEmergency: () => void }) {
  const { t } = useTranslation();
  // Per design-system 05-flows.md §5.4 Step 0: surface the full emergency trio
  // (police / ambulance / fire) one tap from the landing screen, plus a visible
  // "talk to a person" hand-off (Principle 12). Immediate dial, no hold.
  const emergencyNumbers: { num: string; labelKey: string }[] = [
    { num: '100', labelKey: 'accident.call_police' },
    { num: '101', labelKey: 'accident.call_ambulance' },
    { num: '102', labelKey: 'accident.call_fire' },
  ];
  return (
    <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 text-base text-amber-900">
      <p className="font-medium mb-2">{t('accident.disclaimer_title')}</p>
      <p>{t('accident.disclaimer_body')}</p>
      <div className="mt-3 grid grid-cols-3 gap-2">
        {emergencyNumbers.map(({ num, labelKey }) => (
          <a
            key={num}
            href={`tel:${num}`}
            className="rounded-xl bg-red-600 text-white font-semibold py-3 text-center min-h-[48px] flex flex-col items-center justify-center leading-tight"
          >
            <span className="text-[18px] tabular-nums">{num}</span>
            <span className="text-[13px] font-medium opacity-90">{t(labelKey)}</span>
          </a>
        ))}
      </div>
      <button
        onClick={onEmergency}
        className="mt-3 w-full text-[15px] text-amber-900 underline underline-offset-2 py-2 min-h-[44px]"
      >
        {t('accident.talk_to_person')}
      </button>
    </div>
  );
}

function StartStep({
  onContinue,
  onInjury,
  pending,
}: {
  onContinue: () => void;
  onInjury: () => void;
  pending: boolean;
}) {
  const { t } = useTranslation();
  return (
    <section className="rounded-2xl bg-white border border-carnaf-line p-5 space-y-4">
      <h2 className="text-lg font-semibold text-carnaf-ink">{t('accident.start_title')}</h2>
      <p className="text-carnaf-ink-muted text-base">{t('accident.start_question')}</p>
      <div className="space-y-3">
        <button
          onClick={onContinue}
          disabled={pending}
          className="w-full rounded-2xl bg-carnaf-teal text-white font-semibold py-4 disabled:opacity-50"
        >
          {t('accident.start_no_injury')}
        </button>
        {/* Serious but NOT emergency-red: red (bg-red-600) is reserved for the
            three 100/101/102 dial buttons (design-system 05-flows §5.3/§5.6).
            Sitting under those, an injury answer in the same red reads as a
            fourth call button and invites a mis-tap. Amber keeps it distinct. */}
        <button
          onClick={onInjury}
          disabled={pending}
          className="w-full rounded-2xl border-2 border-amber-500 bg-amber-50 text-amber-900 font-semibold py-4 disabled:opacity-50"
        >
          {t('accident.start_injury')}
        </button>
      </div>
    </section>
  );
}

function PhotoStep({
  promptIndex,
  photosTaken,
  totalPrompts,
  onPhoto,
  onDone,
  pending,
}: {
  promptIndex: number;
  photosTaken: number;
  totalPrompts: number;
  onPhoto: (file: File) => void;
  onDone: () => void;
  pending: boolean;
}) {
  const { t } = useTranslation();
  const promptKey = PHOTO_PROMPTS[promptIndex]?.label ?? 'accident.photos.front';

  return (
    <section className="rounded-2xl bg-white border border-carnaf-line p-5 space-y-4">
      <div className="flex items-baseline justify-between">
        <h2 className="text-lg font-semibold text-carnaf-ink">{t('accident.photo_title')}</h2>
        <span className="text-[15px] text-carnaf-ink-muted">
          {photosTaken} / {totalPrompts}
        </span>
      </div>
      <p className="text-carnaf-ink-muted text-base">{t(promptKey)}</p>

      <label className="block rounded-2xl border-2 border-dashed border-carnaf-line p-6 text-center cursor-pointer bg-carnaf-paper-sunk hover:border-carnaf-teal">
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          capture="environment"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onPhoto(f);
            e.target.value = ''; // allow re-selecting the same file
          }}
        />
        <div className="text-carnaf-ink font-semibold">{pending ? t('common.loading') : t('accident.take_photo')}</div>
      </label>

      {photosTaken >= 2 && (
        <button
          onClick={onDone}
          disabled={pending}
          className="w-full rounded-2xl bg-carnaf-teal text-white font-semibold py-3 disabled:opacity-50"
        >
          {t('accident.photo_done')}
        </button>
      )}
    </section>
  );
}

function PartyStep({
  onSubmit,
  onSkip,
  pending,
}: {
  onSubmit: (data: {
    name?: string;
    plate?: string;
    insurerName?: string;
    insurancePolicy?: string;
    phone?: string;
  }) => void;
  onSkip: () => void;
  pending: boolean;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [plate, setPlate] = useState('');
  const [insurerName, setInsurerName] = useState('');
  const [insurancePolicy, setInsurancePolicy] = useState('');
  const [phone, setPhone] = useState('');

  return (
    <section className="rounded-2xl bg-white border border-carnaf-line p-5 space-y-4">
      <h2 className="text-lg font-semibold text-carnaf-ink">{t('accident.party_title')}</h2>
      <p className="text-carnaf-ink-muted text-base">{t('accident.party_hint')}</p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit({
            name: name.trim() || undefined,
            plate: plate.trim() || undefined,
            insurerName: insurerName.trim() || undefined,
            insurancePolicy: insurancePolicy.trim() || undefined,
            phone: phone.trim() || undefined,
          });
        }}
        className="space-y-3"
      >
        <Input label={t('accident.party.name')} value={name} onChange={setName} />
        <Input label={t('accident.party.plate')} value={plate} onChange={setPlate} />
        <Input label={t('accident.party.insurer')} value={insurerName} onChange={setInsurerName} />
        <Input label={t('accident.party.policy')} value={insurancePolicy} onChange={setInsurancePolicy} />
        <Input label={t('accident.party.phone')} value={phone} onChange={setPhone} type="tel" />
        <button
          type="submit"
          disabled={pending}
          className="w-full rounded-2xl bg-carnaf-teal text-white font-semibold py-3 disabled:opacity-50"
        >
          {pending ? t('common.loading') : t('accident.party_save')}
        </button>
        <button type="button" onClick={onSkip} disabled={pending} className="w-full text-sm text-carnaf-ink-muted">
          {t('accident.party_skip')}
        </button>
      </form>
    </section>
  );
}

function ClaimStep({
  claim,
  onRequestTow,
  onRequestReplacement,
  onDone,
  pending,
}: {
  claim: ClaimSummary;
  onRequestTow: () => void;
  onRequestReplacement: () => void;
  onDone: () => void;
  pending: boolean;
}) {
  const { t } = useTranslation();
  return (
    <section className="rounded-2xl bg-white border border-carnaf-line p-5 space-y-4">
      <h2 className="text-lg font-semibold text-carnaf-ink">{t('accident.claim_title')}</h2>

      {claim.claimReference ? (
        <div className="rounded-xl bg-carnaf-teal/10 border border-carnaf-teal/30 p-3">
          <p className="text-[15px] uppercase tracking-wide text-carnaf-ink-muted">{t('accident.claim_reference')}</p>
          <p className="font-mono font-semibold text-carnaf-ink mt-0.5">{claim.claimReference}</p>
          {claim.packetReference && (
            <p className="text-[15px] text-carnaf-ink-muted mt-2">
              {t('accident.packet_sent', { ref: claim.packetReference })}
            </p>
          )}
        </div>
      ) : (
        <p className="text-carnaf-ink-muted text-sm">{t('accident.claim_hint')}</p>
      )}

      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={onRequestTow}
          disabled={pending || !!claim.tow}
          className="rounded-2xl border-2 border-carnaf-teal text-carnaf-teal-dark font-semibold py-3 disabled:opacity-50"
        >
          {claim.tow ? t('accident.tow_eta', { eta: claim.tow.etaMinutes }) : t('accident.request_tow')}
        </button>
        <button
          onClick={onRequestReplacement}
          disabled={pending || !!claim.replacement}
          className="rounded-2xl border-2 border-carnaf-teal text-carnaf-teal-dark font-semibold py-3 disabled:opacity-50"
        >
          {claim.replacement
            ? t('accident.replacement_eta', { eta: claim.replacement.etaMinutes })
            : t('accident.request_replacement')}
        </button>
      </div>

      <button onClick={onDone} className="w-full text-sm text-carnaf-ink-muted underline pt-2">
        {t('accident.close_flow')}
      </button>
    </section>
  );
}

function ResolvedStep({ claim, onClose }: { claim: ClaimSummary; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <section className="rounded-2xl bg-white border border-carnaf-line p-6 text-center space-y-4">
      <h2 className="text-xl font-semibold text-carnaf-teal-dark">{t('accident.resolved_title')}</h2>
      {claim.claimReference && <p className="font-mono text-carnaf-ink">{claim.claimReference}</p>}
      <button onClick={onClose} className="w-full rounded-2xl bg-carnaf-teal text-white font-semibold py-3">
        {t('accident.back_to_vehicle')}
      </button>
    </section>
  );
}

function EscalatedStep() {
  const { t } = useTranslation();
  return (
    <section className="rounded-2xl bg-red-50 border border-red-200 p-6 text-center space-y-3">
      <h2 className="text-xl font-semibold text-red-800">{t('accident.escalated_title')}</h2>
      <p className="text-sm text-red-800">{t('accident.escalated_sub')}</p>
      <a href="tel:101" className="inline-block rounded-2xl bg-red-600 text-white font-semibold py-3 px-6">
        {t('accident.emergency_call')}
      </a>
    </section>
  );
}

// Suppress react-hooks dep warning by not depending on i18n.language outside callbacks.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function _useNoop() {
  useEffect(() => undefined, []);
}

function Input({
  label,
  value,
  onChange,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
}) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-carnaf-ink mb-1.5">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-carnaf-line bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-carnaf-teal"
      />
    </label>
  );
}
